import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Table, Typography, message, Spin } from "antd";
import { RightOutlined, CloseOutlined } from "@ant-design/icons";
import {
  getOrderDetail,
  getOrderAuditRecord,
  ORDER_STATUS_DICT,
  ORDER_AUDIT_STATUS_DICT,
  OrderStatus,
  OrderAuditStatus,
  downloadOrderFormPdf,
} from "@ifa/utils";
import type { OrderProcessFlowRecordVO, FlowRecord } from "@ifa/utils";
import styles from "./OrderForm.module.css";
import correctionStyles from "./OrderCorrectionList.module.css";
import { SendCorrectionModal } from "../components/SendCorrectionModal";
import { FilePreviewModal } from "../components/FilePreviewModal";
import CustomCard from "../components/CustomCard";
import CustomButton from "../components/CustomButton";
import copyIcon from "../assets/svg/copy.svg";
import { getOrderChangeHistoryWithFallback } from "../services/orderChangeHistory";

const { Text } = Typography;

interface CorrectionRecord extends FlowRecord {
  status: "pending_info" | "pending_customer" | "other";
  items?: {
    module: string;
    before: string;
    after: string;
  }[];
}

const STATUS_MAP: Record<string, { text: string; color: string }> = {
  pending_info: { text: "待修改資料", color: "orange" },
  pending_customer: { text: "待客戶確認", color: "blue" },
  other: { text: "其他", color: "default" },
};

// Determine record status based on new logic
const determineRecordStatus = (record: FlowRecord): CorrectionRecord["status"] => {
  const { newOrderStatus, newOrderAuditStatus } = record;

  // 待修改資料: 新訂單狀態為 ORDER_AUDIT，且新審核狀態為 PENDING_MODIFICATION
  if (newOrderStatus === OrderStatus.OrderAudit && newOrderAuditStatus === OrderAuditStatus.PendingModification) {
    return "pending_info";
  }

  // 待客戶確認: 新訂單狀態或新審核狀態進入 PENDING_CONFIRMATION
  if (newOrderStatus === OrderStatus.PendingConfirmation || newOrderAuditStatus === OrderAuditStatus.PendingConfirmation) {
    return "pending_customer";
  }

  return "other";
};

export const OrderCorrectionList: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();

  const [orderData, setOrderData] = useState<any>(null);
  const [records, setRecords] = useState<CorrectionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState("all");
  const contentRef = useRef<HTMLDivElement>(null);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<CorrectionRecord | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string>("");
  const [previewFileName, setPreviewFileName] = useState<string>("");

  useEffect(() => {
    if (orderId) {
      setLoading(true);

      // Fetch Order Detail
      getOrderDetail(Number(orderId)).then((res) => {
        setOrderData(res);
      });

      // Fetch Audit Records
      getOrderAuditRecord(Number(orderId))
        .then(async (res: OrderProcessFlowRecordVO) => {
          if (res && res.flowRecords) {
            // No longer filtering by targetStatuses to show all records as per user request
            // const targetStatuses = [...];
            // const relevantRecords = res.flowRecords.filter((r) => r.newOrderAuditStatus && targetStatuses.includes(r.newOrderAuditStatus));
            const relevantRecords = res.flowRecords;

            // Enhance records with change history if available
            const enhancedRecords = await Promise.all(
              relevantRecords.map(async (record) => {
                const status = determineRecordStatus(record);
                const items: CorrectionRecord["items"] = [];

                if (record.canViewChangeHistory && record.flowRecordId) {
                  try {
                    const changes = await getOrderChangeHistoryWithFallback(Number(orderId), record.flowRecordId);
                    // changes 是一个对象，包含 changeDetails 数组
                    if (changes && changes.changeDetails && changes.changeDetails.length > 0) {
                      // 直接使用 changeDetails
                      changes.changeDetails.forEach((detail) => {
                        items?.push({
                          module: detail.fieldName || detail.field || "未知項目",
                          before: detail.oldValue || "-",
                          after: detail.newValue || "-",
                        });
                      });
                    }
                  } catch (e) {
                    console.error("Failed to fetch change history for record", record.flowRecordId, e);
                  }
                }

                return {
                  ...record,
                  status,
                  items,
                } as CorrectionRecord;
              }),
            );

            // Sort by createTime desc
            enhancedRecords.sort((a, b) => {
              const timeA = a.createTime ? new Date(a.createTime).getTime() : 0;
              const timeB = b.createTime ? new Date(b.createTime).getTime() : 0;
              return timeB - timeA;
            });

            setRecords(enhancedRecords);
          }
        })
        .catch((err) => {
          console.error(err);
          // message.error("獲取審核記錄失敗");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [orderId]);

  const handleBack = () => {
    navigate("/orders");
  };

  const handleCorrectOrder = (recordId: string) => {
    // Navigate to Correction Form with source information
    navigate(`/orders/${orderId}/correction/${recordId}/edit`, {
      state: { from: "correction" },
    });
  };

  const handleAuditLog = () => {
    // Navigate to Audit Log with source information
    navigate(`/orders/${orderId}/audit-log`, {
      state: { from: "correction" },
    });
  };

  const handleMenuClick = (key: string) => {
    setSelectedKey(key);
    // Always scroll to top when switching filters
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const filteredRecords = selectedKey === "all" ? records : records.filter((r) => r.status === selectedKey);
  const firstPendingInfoRecordId = records.find((record) => record.status === "pending_info")?.flowRecordId;

  const handlePreviewDocument = async (record: CorrectionRecord) => {
    if (!orderId || !record.lastModifiedFlowRecordId) {
      // message.error("暫無文件可預覽");
      return;
    }
    try {
      const result = await downloadOrderFormPdf({
        orderId: Number(orderId),
        formType: "SUPPLEMENTARY_INFORMATION_FORM",
        flowRecordId: record.lastModifiedFlowRecordId,
      });
      if (result && result.blob) {
        const url = URL.createObjectURL(result.blob);
        setPreviewFileUrl(url);
        console.log(url);
        setPreviewFileName(result.filename || "批改檔案.pdf");
        setPreviewOpen(true);
      }
    } catch (err) {
      console.error(err);
      // message.error("預覽失敗");
    }
  };

  const handleOpenSendModal = (record: CorrectionRecord) => {
    setCurrentRecord(record);
    setSendModalOpen(true);
  };

  const renderCard = (item: CorrectionRecord, index: number) => {
    const statusConfig = STATUS_MAP[item.status];

    // For 'other' status, try to use the dictionary label
    const displayStatusText = item.status === "other" && item.newOrderAuditStatus ? ORDER_AUDIT_STATUS_DICT[item.newOrderAuditStatus] || statusConfig.text : statusConfig.text;

    // 當訂單當前處於待確認時，不顯示批改訂單按鈕
    const isPendingConfirmation = orderData?.orderAuditStatus === OrderAuditStatus.PendingConfirmation;

    // 只有待修改資料狀態且是第一條記錄時才顯示"批改訂單"按鈕
    const showEditButton = item.status === "pending_info" && index === 0 && !isPendingConfirmation;
    const showSendButton = item.status === "pending_info" && item.flowRecordId === firstPendingInfoRecordId;

    // 渲染支持换行的文本
    const renderTextWithLineBreaks = (text: string) => {
      if (!text) return <b>-</b>;
      return (
        <b>
          {text.split(/\\n|\n/).map((line, index, array) => (
            <React.Fragment key={index}>
              {line}
              {index < array.length - 1 && <br />}
            </React.Fragment>
          ))}
        </b>
      );
    };

    const columns = [
      { title: "模塊及項目", dataIndex: "module", key: "module" },
      { 
        title: "批改前內容", 
        dataIndex: "before", 
        key: "before", 
        render: renderTextWithLineBreaks 
      },
      { 
        title: "批改後內容", 
        dataIndex: "after", 
        key: "after", 
        render: renderTextWithLineBreaks 
      },
    ];

    // 只展示前三行数据
    const displayItems = item.items && item.items.length > 0 ? item.items.slice(0, 3) : [];

    return (
      <CustomCard key={item.flowRecordId} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <Tag color={statusConfig.color} style={{ fontSize: 14, padding: "4px 12px" }}>
            {displayStatusText}
          </Tag>
          <div style={{ color: "#999", fontSize: 12 }}>
            <span style={{ marginRight: 16 }}>操作人: {item.creatorName || "-"}</span>
            <span>時間: {item.createTime || "-"}</span>
          </div>
        </div>

        <div style={{ background: "#FAFAFA", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          {displayItems.length > 0 ? (
            <Table 
              dataSource={displayItems} 
              columns={columns} 
              pagination={false} 
              size="small" 
              showHeader={true} 
              bordered={false} 
              rowKey={(record) => record.module + record.before} 
            />
          ) : (
            <div style={{ color: "#999", textAlign: "center", padding: 16 }}>無修改內容</div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">審核意見: </Text>
          <Text>{item.description || "無"}</Text>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          {/* 批改訂單按鈕：只在待修改資料且是第一條記錄時顯示 */}
          {showEditButton && (
            <CustomButton bgColor="white" color="var(--theme-primary)" style={{ border: "1px solid var(--theme-primary)" }} onClick={() => handleCorrectOrder(String(item.flowRecordId))}>
              批改訂單
            </CustomButton>
          )}

          <CustomButton bgColor="var(--theme-primary)" onClick={handleAuditLog}>
            審核及批改記錄
          </CustomButton>

          {item.signature && (
            <CustomButton bgColor="var(--theme-primary)" onClick={() => handlePreviewDocument(item)}>
              預覽批改檔案
            </CustomButton>
          )}

          {/* 發送給投保人確認按鈕：只顯示在總記錄列表中第一條待修改資料上 */}
          {showSendButton && (
            <CustomButton bgColor="var(--theme-primary)" onClick={() => handleOpenSendModal(item)} disabled={item.status === "pending_info" && (!item.items || item.items.length === 0)}>

              發送給投保人確認
            </CustomButton>
          )}
        </div>
      </CustomCard>
    );
  };

  // Calculate counts
  const counts = {
    pending_info: records.filter((r) => r.status === "pending_info").length,
    pending_customer: records.filter((r) => r.status === "pending_customer").length,
    other: records.filter((r) => r.status === "other").length,
    all: records.length,
  };

  return (
    <div className={styles["order-form-container"]} style={{ height: "100vh", flexDirection: "column", background: "#f5f5f5" }}>
      <div className={styles["sticky-header-container"]}>
        <header className={styles["order-header"]}>
          <div className={styles["header-left"]}>
            <div className={styles["header-title"]}>
              <span className={styles["header-breadcrumb"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer" }}>
                訂單
              </span>
              <RightOutlined className={styles["header-separator"]} style={{ fontSize: "12px" }} />
              <span>訂單紀錄/批改訂單</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            {/* Order Info */}
            {orderData && (
              <div className={styles["header-info"]}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                  <span className={`${styles["status-tag"]} ${styles[`status-${orderData.orderStatus || OrderStatus.Draft}`]}`}>
                    {ORDER_STATUS_DICT[orderData.orderStatus as OrderStatus] || "草稿"}
                  </span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className={styles["info-item"]} style={{ fontSize: 12 }}>
                      <span className={styles["info-label"]}>編號:</span> {orderData.orderNo || "-"}
                    </span>
                    {orderData.orderNo && (
                      <img
                        src={copyIcon}
                        alt="copy"
                        style={{ marginLeft: 4, cursor: "pointer", width: 12, height: 12 }}
                        onClick={() => {
                          navigator.clipboard.writeText(orderData.orderNo || "");
                          message.success("已複製");
                        }}
                      />
                    )}
                  </div>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", margin: "0 24px" }} />
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span className={styles["info-item"]} style={{ fontSize: 12, lineHeight: "14px", color: "#999" }}>
                    投保人 / 受保人 :
                  </span>
                  <span className={styles["info-item"]} style={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, color: "#333" }}>
                    {orderData.policyholder?.basic?.chineseSurname || "-"} {orderData.policyholder?.basic?.chineseGivenName || "-"} <span style={{ color: "#E8E8E8", margin: "0 8px" }}>/</span>{" "}
                    {orderData.insured?.basic?.chineseSurname || "-"} {orderData.insured?.basic?.chineseGivenName || "-"}
                  </span>
                </div>
                <div style={{ width: 1, height: 32, background: "#E8E8E8", marginLeft: 24 }} />
              </div>
            )}
            <div className={styles["header-close"]} onClick={handleBack} style={{ marginLeft: 16, cursor: "pointer" }}>
              <CloseOutlined style={{ fontSize: 18, color: "#999" }} />
            </div>
          </div>
        </header>
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        {/* 左侧筛选导航栏 - 使用类似 ProductList 的样式 */}
        <div className={correctionStyles.correctionSidebar}>
          {/* <div className={correctionStyles.sidebarTitle}>篩選條件</div> */}
          <div className={correctionStyles.filterList}>
            <div className={`${correctionStyles.filterItem} ${selectedKey === "pending_info" ? correctionStyles.active : ""}`} onClick={() => handleMenuClick("pending_info")}>
              <span className={correctionStyles.filterLabel}>待修改資料</span>
              <span className={correctionStyles.filterCount}>{counts.pending_info}</span>
            </div>

            <div className={`${correctionStyles.filterItem} ${selectedKey === "pending_customer" ? correctionStyles.active : ""}`} onClick={() => handleMenuClick("pending_customer")}>
              <span className={correctionStyles.filterLabel}>待客戶確認</span>
              <span className={correctionStyles.filterCount}>{counts.pending_customer}</span>
            </div>

            <div className={`${correctionStyles.filterItem} ${selectedKey === "all" ? correctionStyles.active : ""}`} onClick={() => handleMenuClick("all")}>
              <span className={correctionStyles.filterLabel}>全部</span>
              <span className={correctionStyles.filterCount}>{counts.all}</span>
            </div>
          </div>
        </div>

        <div ref={contentRef} className={correctionStyles.correctionContent}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 100 }}>
              <Spin size="large" />
            </div>
          ) : (
            filteredRecords.map((record, index) => renderCard(record, index))
          )}
        </div>
      </div>
      {currentRecord && (
        <SendCorrectionModal
          open={sendModalOpen}
          onCancel={() => setSendModalOpen(false)}
          orderId={Number(orderId)}
        />
      )}

      <FilePreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          if (previewFileUrl) {
            URL.revokeObjectURL(previewFileUrl);
          }
        }}
        fileUrl={previewFileUrl}
        fileName={previewFileName}
        fileType="pdf"
      />
    </div>
  );
};
